import React, { useRef, useEffect, useState } from "react";
import { Player } from "./components/Player.ts";
import { Pawn } from "./components/Pawn.ts";
import { PLAYER_CONSTANTS } from "./constants.tsx";
import { InputHandler } from "./components/InputHandler.ts";
import { CollisionManager } from "./components/CollisionManager.ts";
import { GameLoop } from "./components/GameLoop.ts";
import { GameCamera } from "./components/GameCamera.ts";
import { GameRenderer } from "./components/GameRenderer.ts";
import { GameStateManager, GameState } from "./components/GameStateManager.ts";
import { AudioManager } from "./components/AudioManager.ts";
import { WorkHistoryEntry } from "./components/WorkHistoryEntry.tsx";
import { PersonalEntry } from "./components/PersonalEntry.tsx";
import { HobbiesEntry } from "./components/HobbiesEntry.tsx";
import { RandomEntry } from "./components/RandomEntry.tsx";
import { ControlsIndicator } from "./components/ControlsIndicator.tsx";
import { ObjectiveTracker } from "./components/ObjectiveTracker.tsx";
import { MuteButton } from "./components/MuteButton.tsx";
import { WelcomeDialog } from "./components/WelcomeDialog.tsx";
import "./App.css";

function App() {
  const mapRef = useRef<HTMLCanvasElement>(null);
  const [gameState, setGameState] = useState<GameState>('playing');
  const [gameLoop, setGameLoop] = useState<GameLoop | null>(null);
  const [entryPosition, setEntryPosition] = useState<{ x: number; y: number } | undefined>();
  const [inputHandler, setInputHandler] = useState<InputHandler | null>(null);
  const [pressedKeys, setPressedKeys] = useState({
    KeyW: false,
    KeyA: false,
    KeyS: false,
    KeyD: false
  });
  const audioManagerRef = useRef<AudioManager | null>(null);
  const [visitedEntries, setVisitedEntries] = useState<Set<string>>(new Set());
  const [showWelcome, setShowWelcome] = useState(true);
  const [tourMode, setTourMode] = useState(false);

  useEffect(() => {
    const canvas = mapRef.current;
    if (canvas) {
      const initializeGame = async () => {
        const gameRenderer = new GameRenderer(canvas);
        const gameCamera = new GameCamera();
        const player = new Player();
        const inputHandler = new InputHandler();
        const collisionManager = new CollisionManager();
        const gameStateManager = new GameStateManager();
        const audioManager = new AudioManager();
        audioManagerRef.current = audioManager;

        setInputHandler(inputHandler);

        gameStateManager.setStateChangeCallback((newState: GameState) => {
          setGameState(newState);
          audioManager.skipToNext();
          if (newState.startsWith('entry-')) {
            setVisitedEntries((prev: Set<string>) => new Set(prev).add(newState));
            const savedPosition = gameStateManager.getSavedPosition();
            if (savedPosition) {
              setEntryPosition({
                x: savedPosition.entryPoint.mesh.position.x,
                y: savedPosition.entryPoint.mesh.position.y
              });
            }
          }
        });

        const pawn = new Pawn(
          PLAYER_CONSTANTS.INIT_X + 60,
          PLAYER_CONSTANTS.INIT_Y,
          collisionManager,
          player.sprite
        );
        const pawns = [pawn];

        player.addToScene(gameRenderer.getScene());
        pawns.forEach(p => p.addToScene(gameRenderer.getScene()));
        gameRenderer.addCollidersToScene(collisionManager);

        const background = await gameRenderer.loadBackground();

        const newGameLoop = new GameLoop(
          player,
          inputHandler,
          collisionManager,
          gameStateManager,
          gameRenderer.getRenderer(),
          gameCamera.getCamera(),
          gameRenderer.getScene(),
          background,
          pawns
        );

        setGameLoop(newGameLoop);
        newGameLoop.start();
      };

      initializeGame();
    }
  }, []);

  // Start audio on first user interaction
  useEffect(() => {
    const startAudio = () => {
      audioManagerRef.current?.start();
      window.removeEventListener('keydown', startAudio);
      window.removeEventListener('click', startAudio);
    };
    window.addEventListener('keydown', startAudio);
    window.addEventListener('click', startAudio);
    return () => {
      window.removeEventListener('keydown', startAudio);
      window.removeEventListener('click', startAudio);
    };
  }, []);

  // Update pressed keys state
  useEffect(() => {
    if (!inputHandler) return;

    const updateKeys = () => {
      const keys = inputHandler.getKeys();
      setPressedKeys(keys);
    };

    const interval = setInterval(updateKeys, 16); // ~60fps
    return () => clearInterval(interval);
  }, [inputHandler]);

  const TOUR_SEQUENCE: GameState[] = ['entry-personal', 'entry-work', 'entry-hobbies', 'entry-random'];

  const handleReturnToMap = () => {
    setTourMode(false);
    if (gameLoop) {
      gameLoop.restorePosition();
    }
    setGameState('playing');
    setEntryPosition(undefined);
  };

  const handleWelcomeDismiss = () => {
    setShowWelcome(false);
    audioManagerRef.current?.start();
  };

  const handleSkipToTour = () => {
    setShowWelcome(false);
    setTourMode(true);
    setGameState('entry-personal');
    setVisitedEntries(prev => new Set(prev).add('entry-personal'));
    audioManagerRef.current?.start();
  };

  const handleTourNext = () => {
    const currentIndex = TOUR_SEQUENCE.indexOf(gameState);
    if (currentIndex < TOUR_SEQUENCE.length - 1) {
      const next = TOUR_SEQUENCE[currentIndex + 1];
      setGameState(next);
      setVisitedEntries(prev => new Set(prev).add(next));
    }
  };

  const getTourNextLabel = (): string | undefined => {
    if (!tourMode) return undefined;
    const labels: Record<string, string> = {
      'entry-personal': 'Work History',
      'entry-work': 'Hobbies',
      'entry-hobbies': 'Projects',
    };
    return labels[gameState];
  };

  return (
    <>
      {showWelcome && <WelcomeDialog onStart={handleWelcomeDismiss} onSkip={handleSkipToTour} />}
      <canvas
        ref={mapRef}
        id="wes_com"
        style={{ display: gameState === 'playing' ? 'block' : 'none' }}
      ></canvas>
      {gameState === 'playing' && <ControlsIndicator pressedKeys={pressedKeys} inputHandler={inputHandler} />}
      {gameState === 'playing' && <ObjectiveTracker visitedCount={visitedEntries.size} />}
      {gameState === 'playing' && audioManagerRef.current && <MuteButton audioManager={audioManagerRef.current} />}
      {gameState === 'entry-work' && (
        <WorkHistoryEntry
          onReturnToMap={handleReturnToMap}
          entryPosition={entryPosition}
          onNext={getTourNextLabel() ? handleTourNext : undefined}
          nextLabel={getTourNextLabel()}
        />
      )}
      {gameState === 'entry-personal' && (
        <PersonalEntry
          onReturnToMap={handleReturnToMap}
          entryPosition={entryPosition}
          onNext={getTourNextLabel() ? handleTourNext : undefined}
          nextLabel={getTourNextLabel()}
        />
      )}
      {gameState === 'entry-hobbies' && (
        <HobbiesEntry
          onReturnToMap={handleReturnToMap}
          entryPosition={entryPosition}
          onNext={getTourNextLabel() ? handleTourNext : undefined}
          nextLabel={getTourNextLabel()}
        />
      )}
      {gameState === 'entry-random' && (
        <RandomEntry
          onReturnToMap={handleReturnToMap}
          entryPosition={entryPosition}
          onNext={getTourNextLabel() ? handleTourNext : undefined}
          nextLabel={getTourNextLabel()}
        />
      )}
    </>
  );
}

export default App;
